import type { RouteMeta, RouteRecordRaw } from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    /** Which shell wraps the page (see App.vue). */
    layout?: 'guest' | 'auth' | 'app' | 'admin'
    /** Anonymous visitors are sent to the login page. */
    requiresAuth?: boolean
    /** Authenticated users are sent to the home. */
    guestOnly?: boolean
    /** Admin panel: needs the `admin` *or* the `editor` flag. */
    requiresStaff?: boolean
    /** Admin-only section of the panel: editors are bounced to the panel home. */
    requiresAdmin?: boolean
    /**
     * Still reachable while the site is in maintenance. Only login and the
     * notice page are — the panel is covered by the staff check instead.
     */
    availableDuringMaintenance?: boolean
    /** i18n key used for `document.title`. */
    titleKey?: string
    /**
     * Overrides applied once signed in, for a page that is a different page
     * behind the same URL (`/`: landing for a guest, dashboard for a user).
     * Read through `effectiveLayout` / `effectiveTitleKey`, never directly.
     */
    whenAuthenticated?: { layout?: RouteMeta['layout']; titleKey?: string }
  }
}

/** Layout of a route for the current visitor. */
export function effectiveLayout(
  meta: RouteMeta,
  isAuthenticated: boolean,
): NonNullable<RouteMeta['layout']> {
  return (isAuthenticated ? meta.whenAuthenticated?.layout : undefined) ?? meta.layout ?? 'guest'
}

/** `document.title` key of a route for the current visitor. */
export function effectiveTitleKey(meta: RouteMeta, isAuthenticated: boolean): string | undefined {
  return (isAuthenticated ? meta.whenAuthenticated?.titleKey : undefined) ?? meta.titleKey
}

/**
 * Static routes. Feature pages are not listed here: they are declared by the
 * backend menu and resolved by the catch-all `dynamic` route below.
 */
export const routes: RouteRecordRaw[] = [
  {
    // The home, at the bare domain whether signed in or not: HomeView shows the
    // landing to a guest and the dashboard to a user. Neither `guestOnly` nor
    // `requiresAuth`, since both kinds of visitor belong here.
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      layout: 'guest',
      titleKey: 'landing.meta.title',
      whenAuthenticated: { layout: 'app', titleKey: 'common.home' },
    },
  },

  // --- Authentication -------------------------------------------------------
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    // Reachable during maintenance: an admin has to be able to sign in and
    // reopen the site, and the API keeps `auth/*` open for the same reason.
    meta: {
      layout: 'auth',
      guestOnly: true,
      availableDuringMaintenance: true,
      titleKey: 'auth.login.title',
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { layout: 'auth', guestOnly: true, titleKey: 'common.createYourAccount' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { layout: 'auth', guestOnly: true, titleKey: 'auth.forgot.title' },
  },
  {
    // The backend emails `{APP_FE_URL}/reset-password?token=…&email=…`.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { layout: 'auth', guestOnly: true, titleKey: 'auth.reset.title' },
  },
  {
    // The backend emails `{APP_FE_URL}/verify-email?id=…&hash=…`.
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { layout: 'auth', titleKey: 'auth.verify.title' },
  },

  // --- Authenticated area ---------------------------------------------------
  {
    // The home used to live here: kept so bookmarks and old links still land.
    path: '/dashboard',
    redirect: { name: 'home' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'common.profile' },
  },
  {
    path: '/account-settings',
    name: 'account-settings',
    component: () => import('@/views/AccountSettingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'common.accountSettings' },
  },
  {
    // Voce di menu `tuoiVillaggi`: rotta statica perché la pagina esiste
    // davvero, e le statiche vengono valutate prima del catch-all `dynamic`.
    path: '/overview-villages',
    name: 'overview-villages',
    component: () => import('@/views/OverviewVillagesView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'overviewVillages.title' },
  },
  {
    // Liste farm: statistiche dei raid dalle liste incollate dal punto di
    // raduno. Segue il mondo dell'header come `/overview-villages`; senza
    // personaggio rivendicato la view disegna l'avviso. Voce di menu `farmList`.
    path: '/farm-list',
    name: 'farm-list',
    component: () => import('@/views/FarmListView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'farmList.title' },
  },
  {
    // Calcolatore d'invio difese. Segue il mondo selezionato nell'header (non
    // prende un `:id`), ma funziona anche senza: coordinate e velocità a mano.
    path: '/calc-def-send',
    name: 'calc-def-send',
    component: () => import('@/views/CalcDefSendView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'calcDefSend.title' },
  },
  {
    // Calcolatore di contrattacco: come il precedente segue il mondo
    // dell'header senza `:id`, e funziona anche senza.
    path: '/counter-attack',
    name: 'counter-attack',
    component: () => import('@/views/CounterAttackView.vue'),
    meta: { layout: 'app', titleKey: 'counterAttack.title' },
  },
  {
    // Ottimizzazione NPC (il foglio "Sgoldata"): non dipende dal mondo
    // dell'header, che serve solo a proporre la tribù del proprio personaggio.
    path: '/tool/npc-optimization',
    name: 'npc-optimization',
    component: () => import('@/views/NpcOptimizationView.vue'),
    meta: { layout: 'app', titleKey: 'npcOptimization.title' },
  },
  {
    // Ordine di costruzione del grano: aperto anche agli ospiti (il calcolo è
    // tutto lato client), il login serve solo a salvare piani e progressi.
    path: '/tool/crop-order',
    name: 'crop-order',
    component: () => import('@/views/CropOrderView.vue'),
    meta: { layout: 'app', titleKey: 'cropOrder.title' },
  },
  {
    // Guida alla fonda del secondo villaggio: aperta anche agli ospiti (la
    // guida è tutta lato client), il login serve solo a salvare l'avanzamento.
    path: '/tool/guide-start',
    name: 'guide-start',
    component: () => import('@/views/StartGuideView.vue'),
    meta: { layout: 'app', titleKey: 'startGuide.title' },
  },

  // --- Gruppi (self-service) -------------------------------------------------
  // Rotte statiche come le altre pagine vere di quest'area: vengono valutate
  // prima del catch-all `dynamic`.
  {
    path: '/groups',
    name: 'groups-list',
    component: () => import('@/views/GroupsListView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'common.fields.groups' },
  },
  {
    path: '/groups/:id',
    name: 'group-detail',
    component: () => import('@/views/GroupDetailView.vue'),
    // Nessun titleKey: il titolo lo imposta la view, che conosce il nome del gruppo.
    meta: { layout: 'app', requiresAuth: true },
  },
  {
    // `requiresAuth` basta a coprire tutto lo step 1 del wizard di invito
    // (login con `?redirect=` verso questa stessa URL, poi ripresa qui):
    // nessuna logica di guard dedicata.
    path: '/group/invite/:groupId/:key',
    name: 'group-invite',
    component: () => import('@/views/GroupInviteView.vue'),
    meta: { layout: 'app', requiresAuth: true },
  },
  {
    path: '/group/invite/dual/:userId/:groupId/:key',
    name: 'group-invite-dual',
    component: () => import('@/views/GroupInviteDualView.vue'),
    meta: { layout: 'app', requiresAuth: true },
  },
  {
    // Classifiche del gruppo attualmente selezionato — non prende un `:id`,
    // a differenza di `/groups/:id`: è sempre "il mio gruppo", non uno
    // qualsiasi. La voce di menu che ci punta va abilitata solo per chi ha un
    // ruolo in un gruppo (enabled_group_*); la view gestisce da sola il caso
    // di chi ci arriva senza un gruppo selezionato.
    path: '/group/ranking',
    name: 'group-ranking',
    component: () => import('@/views/GroupRankingView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupRanking.title' },
  },
  {
    // I gruppi propri su un server concluso: spariscono da `/groups`,
    // `GroupSelector` e `ProfileView` (vedi i loro commenti) e tornano solo
    // qui. Sola lettura, quindi nessuna candidatura/creazione da mostrare.
    path: '/group/archive',
    name: 'group-archive',
    component: () => import('@/views/GroupArchiveView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groups.archive.title' },
  },
  {
    // Come `/group/ranking`: nessun `:id`, opera sul gruppo attualmente
    // selezionato. Non esiste un flag di routing `requiresGroupAdmin` — la
    // view stessa rimanda a `group-detail` chi non è admin del gruppo
    // selezionato, stesso approccio già usato per "nessun gruppo selezionato".
    path: '/group/settings',
    name: 'group-settings',
    component: () => import('@/views/GroupSettingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'common.groupSettings' },
  },
  {
    // Stesso approccio di `/group/settings`: nessun `:id`, opera sul gruppo
    // selezionato, gate admin dentro la view.
    path: '/group/members',
    name: 'group-members',
    component: () => import('@/views/GroupMembersView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupMembers.title' },
  },
  {
    path: '/group/candidates',
    name: 'group-candidates',
    component: () => import('@/views/GroupCandidatesView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupCandidates.title' },
  },
  {
    // Come `/group/members`: nessun `:id`, opera sul gruppo selezionato, gate
    // di ruolo dentro la view — qui più stretto (admin/def/off, mai il
    // "member" semplice: vedi GroupPolicy::viewTroops lato backend).
    path: '/group/troops',
    name: 'group-troops',
    component: () => import('@/views/GroupTroopsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupTroops.title' },
  },
  {
    // Crop finder of the selected group: 15c/9c tiles pasted from the game or
    // typed by hand, reservable without expiry. Like `/group/settings`: no
    // `:id`, admin gate inside the view (the backend answers 401 to anyone else).
    path: '/group/crop-finder',
    name: 'group-crop-finder',
    component: () => import('@/views/GroupCropFinderView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'cropFinder.title' },
  },
  {
    // Incoming attacks on the caller's character in the selected group. No
    // `:id` either: it follows the group picked in the header, and the view
    // shows its own "no group selected" state.
    path: '/incomings',
    name: 'incomings',
    component: () => import('@/views/IncomingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'incomings.title' },
  },
  {
    // Coordinators' recap: every attack of the selected group, for its admin
    // and def only. Gate inside the view, like `/group/troops`; a wrapper
    // component of its own (not `IncomingsView` with a prop) so that moving
    // between the two pages mounts a fresh instance instead of patching one
    // halfway through its filters.
    path: '/recap-incomings',
    name: 'recap-incomings',
    component: () => import('@/views/RecapIncomingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'incomingsRecap.title' },
  },
  {
    // Def calls of the selected group (plus the associated groups sharing
    // their walls), one tab per type in `?tab=`. No `:id`, like `/incomings`.
    // `?confirm={id}` (older bot messages) moves on to `def-call-detail`.
    path: '/def-call',
    name: 'def-call',
    component: () => import('@/views/DefCallsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'defCalls.title' },
  },
  {
    // One def call with everything about it and every action one tap away,
    // opened from the icon at the start of its row. Seen from the header's
    // group, like the list (`GET /def-calls/{id}?group=`): switching group
    // reloads it, and a call that group can't see says so in place.
    // `?confirm=1` (the bot messages' button) opens the confirm dialog.
    path: '/def-call/:id(\\d+)',
    name: 'def-call-detail',
    component: () => import('@/views/DefCallDetailView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'defCalls.title' },
  },
  {
    // Participation of the selected group's players in the def calls, for
    // its admin/def only: gate inside the view, like `/recap-incomings` (a
    // member without the role is sent back to `/def-call`). Period and tab
    // in the query string.
    path: '/def-call-stat',
    name: 'def-call-stat',
    component: () => import('@/views/DefCallStatsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'defCallStats.title' },
  },
  {
    // Push requests of the selected group (plus those an associated group
    // chose to show it). No `:id`, like `/def-call`. `?confirm={id}` (older bot
    // messages) moves on to `push-request-detail`, `?create=1` opens the
    // admin's form (the "create push request" menu entry).
    path: '/push-requests',
    name: 'push-requests',
    component: () => import('@/views/PushRequestsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'push.title' },
  },
  {
    // One push request in full, same idea as `def-call-detail`: seen from
    // the header's group, opened from the icon at the start of its row.
    // `?confirm=1` (the bot messages' button) opens the record dialog.
    path: '/push-requests/:id(\\d+)',
    name: 'push-request-detail',
    component: () => import('@/views/PushRequestDetailView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'push.title' },
  },
  {
    // Participation of the selected group's players in the push requests,
    // group admin only: gate inside the view, like `/def-call-stat` (a member
    // is sent back to `/push-requests`). Period and tab in the query string.
    path: '/push-monitoring',
    name: 'push-monitoring',
    component: () => import('@/views/PushMonitoringView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'pushStats.title' },
  },
  {
    // Enemy armies tracked by the selected group, one row per village: no
    // `:id`, it follows the header's group like `/incomings`; the view draws
    // the "no group" state. Any member reads and pastes reports.
    path: '/track-enemies',
    name: 'track-enemies',
    component: () => import('@/views/TrackEnemiesView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'trackEnemies.title' },
  },
  {
    // Reservations of the selected group (tiles to settle, villages to
    // conquer): no `:id`, it follows the header's group like `/incomings`; the
    // view draws the "no group" state and the backend answers 401 to anyone
    // outside the group. `?x=&y=` opens the form on that tile.
    path: '/reservation',
    name: 'reservation',
    component: () => import('@/views/ReservationView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'reservation.title' },
  },
  {
    // Attack plans of the selected group, no `:id` like `/reservation`. The
    // editor and the saved list are for the group's admin/off only: gate
    // inside the views (a member is sent to `/plans/your-plan`), the backend
    // enforces it too. `?edit={id}` / `?duplicate={id}` open a saved plan in
    // the editor.
    path: '/plans/new',
    name: 'plans-new',
    component: () => import('@/views/PlanEditorView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'attackPlans.editor.title' },
  },
  {
    path: '/plans/saved',
    name: 'plans-saved',
    component: () => import('@/views/SavedPlansView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'attackPlans.saved.title' },
  },
  {
    // Any member: the attacks of their own character in the published plans.
    path: '/plans/your-plan',
    name: 'plans-your-plan',
    component: () => import('@/views/YourPlanView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'attackPlans.mine.title' },
  },
  {
    // Come `/group/settings`: nessun `:id`, opera sul gruppo selezionato, gate
    // admin dentro la view. Raggiungibile da un link dentro GroupSettingsView,
    // non da una voce di menu propria.
    path: '/group/discord/settings',
    name: 'group-discord-settings',
    component: () => import('@/views/GroupDiscordSettingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupDiscord.title' },
  },
  {
    // Stesso trattamento di /group/discord/settings, vedi il commento sopra.
    path: '/group/telegram/settings',
    name: 'group-telegram-settings',
    component: () => import('@/views/GroupTelegramSettingsView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'groupTelegram.title' },
  },

  // --- Admin panel ----------------------------------------------------------
  // Not menu-driven: these pages exist for admins and editors regardless of what
  // `GET /menu` returns, so they are declared statically and gated by role.
  {
    path: '/admin',
    name: 'admin-home',
    component: () => import('@/views/admin/AdminHomeView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'admin.home.title' },
  },
  {
    path: '/admin/menus',
    name: 'admin-menus',
    component: () => import('@/views/admin/AdminMenusView.vue'),
    meta: { layout: 'admin', requiresAdmin: true, titleKey: 'common.fields.menu' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/AdminUsersView.vue'),
    meta: { layout: 'admin', requiresAdmin: true, titleKey: 'common.fields.users' },
  },
  {
    path: '/admin/news',
    name: 'admin-news',
    component: () => import('@/views/admin/AdminNewsView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'common.fields.news' },
  },
  {
    path: '/admin/servers',
    name: 'admin-servers',
    component: () => import('@/views/admin/AdminServersView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'common.fields.servers' },
  },
  {
    // Rotta unica per lingue e label, con le due schede in query string: una sola
    // voce di sidebar, e creare o disattivare una lingua aggiorna le colonne
    // della griglia label senza cambiare pagina.
    path: '/admin/labels',
    name: 'admin-labels',
    component: () => import('@/views/admin/AdminLabelsView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'common.fields.labels' },
  },
  {
    path: '/admin/jobs',
    name: 'admin-jobs',
    component: () => import('@/views/admin/AdminJobsView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'admin.jobs.title' },
  },
  {
    path: '/admin/groups',
    name: 'admin-groups',
    component: () => import('@/views/admin/AdminGroupsView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'common.fields.groups' },
  },
  {
    // Catalogo eventi Discord (def call, push, ...): un capogruppo sceglie
    // solo canale/ruolo per un evento gia' qui censito, non ne crea di nuovi.
    path: '/admin/discord',
    name: 'admin-discord',
    component: () => import('@/views/admin/AdminDiscordEventTypesView.vue'),
    meta: { layout: 'admin', requiresStaff: true, titleKey: 'common.fields.discord' },
  },

  // --- Contact --------------------------------------------------------------
  {
    // Linked from the sidebar foot, which a guest sees too: no `requiresAuth`,
    // whoever cannot sign in is exactly who needs to write to us.
    path: '/contact-us',
    name: 'contact-us',
    component: () => import('@/views/ContactView.vue'),
    meta: { layout: 'app', titleKey: 'contact.title' },
  },

  // --- Privacy & Cookie Policy ----------------------------------------------
  {
    // Public like `/contact-us`, and linked from the same places a guest sees
    // (GuestLayout footer, sidebar foot) plus the registration form: it has to
    // be readable before an account exists.
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/PrivacyPolicyView.vue'),
    meta: { layout: 'app', titleKey: 'privacy.title' },
  },

  // --- User manual ----------------------------------------------------------
  {
    // Linked from the sidebar foot (`FOOTER_LINKS`) like `/contact-us`: public,
    // a guest deciding whether to sign up is one of its readers. Topics are
    // deep-linkable as `/guides#guide-<id>`.
    path: '/guides',
    name: 'guides',
    component: () => import('@/views/GuidesView.vue'),
    meta: { layout: 'app', titleKey: 'guides.title' },
  },

  // --- News -----------------------------------------------------------------
  // Pagine vere, quindi rotte statiche: vengono valutate prima del catch-all.
  {
    path: '/news',
    name: 'news-archive',
    component: () => import('@/views/NewsArchiveView.vue'),
    meta: { layout: 'app', titleKey: 'common.fields.news' },
  },
  {
    path: '/news/:id',
    name: 'news-detail',
    component: () => import('@/views/NewsDetailView.vue'),
    // Nessun titleKey: il titolo lo imposta la view, che conosce quello della news.
    meta: { layout: 'app' },
  },

  // --- Player ranking --------------------------------------------------------
  // Vera pagina, quindi rotta statica: nessun requiresAuth/guestOnly, la vede
  // sia un ospite (con il suo picker server in pagina) sia un utente loggato
  // (segue il server selezionato nell'header) — resolveNavigation lascia
  // passare chiunque quando nessuno dei due meta è impostato.
  {
    path: '/rank/players',
    name: 'rank-players',
    component: () => import('@/views/RankPlayersView.vue'),
    meta: { layout: 'app', titleKey: 'rank.players.title' },
  },

  // --- Alliance ranking --------------------------------------------------------
  // Stessa logica di rank-players: rotta statica, pubblica, nessun requiresAuth.
  {
    path: '/rank/alliances',
    name: 'rank-alliances',
    component: () => import('@/views/RankAlliancesView.vue'),
    meta: { layout: 'app', titleKey: 'rank.alliances.title' },
  },

  // --- Ricerca inattivi -------------------------------------------------------
  // Pubblica come le classifiche: l'ospite sceglie il mondo dal picker in
  // pagina, il loggato segue l'header. La voce di menu è `ricercaInattivi`.
  {
    path: '/inactive-search',
    name: 'inactive-search',
    component: () => import('@/views/InactiveSearchView.vue'),
    meta: { layout: 'app', titleKey: 'inactiveSearch.title' },
  },

  // --- Elenco server -----------------------------------------------------------
  // Vera pagina, quindi rotta statica: pubblica come rank-players/rank-alliances,
  // nessun requiresAuth/guestOnly.
  {
    path: '/servers',
    name: 'servers-list',
    component: () => import('@/views/ServersListView.vue'),
    meta: { layout: 'app', titleKey: 'common.fields.servers' },
  },

  // --- Dettaglio server ---------------------------------------------------------
  // Stessa logica di player-detail/alliance-detail: rotta statica, pubblica,
  // nessun titleKey — il titolo lo imposta la view, che conosce il nome del mondo.
  {
    path: '/server/:id',
    name: 'server-detail',
    component: () => import('@/views/ServerDetailView.vue'),
    meta: { layout: 'app' },
  },

  // --- Archivio server ---------------------------------------------------------
  // I server preferiti conclusi vivono qui, non più tra i preferiti "correnti"
  // (header, /servers, dettaglio, profilo): restano preferiti in DB, solo
  // nascosti da quelle superfici. Visibile solo da loggati, a differenza di
  // /servers: non ha senso mostrare l'archivio di qualcun altro a un ospite.
  {
    path: '/archive-servers',
    name: 'archive-servers',
    component: () => import('@/views/ArchiveServersView.vue'),
    meta: { layout: 'app', requiresAuth: true, titleKey: 'archiveServers.title' },
  },

  // --- Mappa del mondo --------------------------------------------------------
  // Stessa logica delle classifiche: rotta statica, pubblica, nessun
  // requiresAuth. Tutto lo stato (server, giocatori, alleanze, villaggi
  // segnalati) sta in query string, perché la composizione è condivisibile con
  // terzi che potrebbero non avere un account.
  {
    path: '/map',
    name: 'map',
    component: () => import('@/views/MapView.vue'),
    meta: { layout: 'app', titleKey: 'map.title' },
  },

  // --- Player detail ----------------------------------------------------------
  // Vera pagina, quindi rotta statica, pubblica come rank-players: la
  // classifica linka qui ma non e' l'unico punto di ingresso possibile (in
  // futuro anche il dettaglio alleanza). Nessun titleKey: il titolo lo
  // imposta la view, che conosce l'alias del player.
  {
    path: '/player/:id',
    name: 'player-detail',
    component: () => import('@/views/PlayerDetailView.vue'),
    meta: { layout: 'app' },
  },

  // --- Alliance detail --------------------------------------------------------
  // Stessa logica di player-detail: rotta statica, pubblica, senza titleKey —
  // il titolo lo imposta la view, che conosce il tag dell'alleanza. Ci si arriva
  // dalla classifica alleanze, ma anche dal dettaglio di un player e dalla
  // tabella membri di un'altra alleanza.
  {
    path: '/alliance/:id',
    name: 'alliance-detail',
    component: () => import('@/views/AllianceDetailView.vue'),
    meta: { layout: 'app' },
  },

  // --- Maintenance notice ---------------------------------------------------
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/MaintenanceView.vue'),
    // The auth shell, not the guest one: it is full-bleed and carries no
    // navigation, which is the point — there is nowhere to navigate to.
    meta: { layout: 'auth', availableDuringMaintenance: true, titleKey: 'maintenance.title' },
  },

  // --- Menu-driven pages and 404 -------------------------------------------
  {
    /**
     * Anything not matched above. The component checks the path against the
     * menu and renders either the feature placeholder or the 404 page, so the
     * URL the user typed stays in the address bar.
     */
    path: '/:pathMatch(.*)*',
    name: 'dynamic',
    component: () => import('@/views/DynamicMenuView.vue'),
    meta: { layout: 'app' },
  },
]
